/** Public, drop-in AI assistant panel for the form editor.
 *
 * Pure composition: a header, a scroll area of chat messages + job progress,
 * and a composer footer. State + I/O live in `useAssistantChat`; toast/UX is
 * wired here so the hook stays UI-framework-agnostic.
 */
import { useEffect, useRef, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useIsDev } from '@/hooks/use-is-dev'
import type { FormDefinition } from '@/features/form-builder/api/form-builder-api'
import { useAssistantChat } from '../hooks/use-assistant-chat'
import { AssistantChatMessage } from './chat-message'
import { AssistantComposer } from './composer'
import { AssistantEmptyState } from './empty-state'
import { AssistantJobProgress } from './job-progress'
import { AssistantModeToggle, type AssistantMode } from './mode-toggle'

const MODE_STORAGE_KEY = 'ai-assistant:mode'

export interface AssistantPanelProps {
  formId: string
  formKey: string
  formTitle: string
  instanceType: 'single' | 'repeatable'
  publishedVersionLabel: string | null
  definition: FormDefinition
  selectedFieldKey: string | null
  onApply: (next: FormDefinition) => void
  onClose: () => void
}

export function AssistantPanel({
  formId, formKey, formTitle, instanceType, publishedVersionLabel,
  definition, selectedFieldKey, onApply, onClose,
}: AssistantPanelProps) {
  const { toast } = useToast()
  const isDev = useIsDev()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<AssistantMode>(() => {
    // Advanced mode is a dev-only affordance. Force simple in production
    // regardless of any value persisted from a prior dev session.
    if (!isDev) return 'simple'
    if (typeof window === 'undefined') return 'simple'
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY)
    return stored === 'advanced' ? 'advanced' : 'simple'
  })

  const changeMode = (m: AssistantMode) => {
    if (!isDev) return
    setMode(m)
    try { window.localStorage.setItem(MODE_STORAGE_KEY, m) } catch { /* ignore */ }
  }

  // Refs so the hook always reads the freshest props at send-time. This is how
  // we keep the per-request payload "latest state only" — history travels as
  // text turns, but the full FormDefinition is rebuilt fresh every call.
  const envelopePropsRef = useRef({
    formId, formKey, formTitle, instanceType, publishedVersionLabel, definition,
  })
  envelopePropsRef.current = { formId, formKey, formTitle, instanceType, publishedVersionLabel, definition }

  const selectedRef = useRef(selectedFieldKey)
  selectedRef.current = selectedFieldKey

  const { turns, job, busy, send, accept, reject } = useAssistantChat({
    getEnvelopeArgs: () => envelopePropsRef.current,
    getSelectedFieldKey: () => selectedRef.current,
    onApply,
    onError: (msg) => toast({ title: 'خطای دستیار', description: msg, variant: 'destructive' }),
    onAccepted: () => toast({ title: 'تغییرات اعمال شد', duration: 1500 }),
  })

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
  }, [turns, job])

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      <header className="shrink-0 border-b border-border bg-card px-4 py-2.5 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">دستیار فرم‌ساز</div>
          <div className="text-[10px] text-muted-foreground truncate">
            درخواست را به زبان طبیعی بنویسید — دستیار یک پچ JSON پیشنهاد می‌دهد.
          </div>
        </div>
        {selectedFieldKey && (
          <Badge variant="outline" className="text-[10px] font-mono" dir="ltr">
            {selectedFieldKey}
          </Badge>
        )}
        {isDev && <AssistantModeToggle mode={mode} onChange={changeMode} />}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </header>

      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-4 py-4 flex flex-col gap-3">
          {turns.length === 0 && <AssistantEmptyState />}
          {turns.map((t, i) => (
            <AssistantChatMessage
              key={i}
              turn={t}
              mode={mode}
              onAccept={() => accept(i)}
              onReject={() => reject(i)}
            />
          ))}
          {job && <AssistantJobProgress job={job} />}
        </div>
      </div>

      <footer className="shrink-0 border-t border-border bg-card px-3 py-2">
        <AssistantComposer busy={busy} onSubmit={send} />
      </footer>
    </div>
  )
}
